import React from 'react';
import { WebSearchResult } from '../types/models';

interface WebSearchSelectionState {
  activeArticle: string | null;
  isSubmitting: boolean;
  error: string | null;
}

interface WebSearchResultsModalProps {
  results: WebSearchResult[];
  onClose: () => void;
  isLoading: boolean;
  onSelect: (result: WebSearchResult) => Promise<void> | void;
  selectionState?: WebSearchSelectionState;
}

const WebSearchResultsModal: React.FC<WebSearchResultsModalProps> = ({ results, onClose, isLoading, onSelect, selectionState }) => {
  const selectionError = selectionState?.error;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-32">
          <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938л3-2.647z"></path>
          </svg>
          <p className="ml-3 text-lg text-gray-700">Выполняется веб-поиск...</p>
        </div>
      );
    }

    if (results.length === 0) {
      return <p>Результаты не найдены.</p>;
    }

    return (
      <div className="space-y-4">
        {results.map((result, index) => {
          const priceValue = typeof result.price === 'number' ? result.price : undefined;
          const formattedPrice =
            priceValue !== undefined && Number.isFinite(priceValue)
              ? `${priceValue.toLocaleString('ru-RU', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} ₽`
              : 'Не указана';
          const itemKey = result.article || result.source_url || `${result.name}-${index}`;
          const isSubmitting = selectionState?.isSubmitting && selectionState.activeArticle === result.article;
          const hasArticle = Boolean(result.article?.trim());

          return (
            <div key={itemKey} className="border border-gray-200 p-4 rounded-md shadow-sm">
              <h3 className="text-lg font-semibold">{result.name}</h3>
              <p className="text-gray-700">Артикул: {result.article}</p>
              <p className="text-gray-700">
                Бренд: {result.brand && result.brand.trim().length > 0 ? result.brand : 'Не указан'}
              </p>
              <p className="text-gray-700">Цена: {formattedPrice}</p>
              <a
                href={result.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm break-all"
              >
                Источник: {result.source_url}
              </a>
              <button
                className="mt-3 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                disabled={!hasArticle || selectionState?.isSubmitting || isLoading}
                onClick={() => onSelect(result)}
              >
                {isSubmitting ? 'Сохранение...' : 'Выбрать этот вариант'}
              </button>
              {!hasArticle && (
                <p className="mt-1 text-xs text-red-500">Выбор недоступен: отсутствует артикул.</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
        <h2 className="text-2xl font-bold mb-4">Результаты веб-поиска</h2>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-600 hover:text-gray-900 text-2xl"
        >
          &times;
        </button>
        {selectionError && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {selectionError}
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );
};

export default WebSearchResultsModal;
